import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type MirrorState =
  | 'normal'
  | 'source_ingress'
  | 'source_egress'
  | 'source_both';
type MirrorSrc = { interface: number; state: MirrorState };
type MirrorPayload = { src: MirrorSrc[]; dest: number };
type PortInfo = { interface: number; name?: string };
type Row = {
  interface: number;
  label: string;
  incoming: boolean;
  outgoing: boolean;
  destination: boolean;
};

function stateFromChecks(incoming: boolean, outgoing: boolean): MirrorState {
  if (incoming && outgoing) return 'source_both';
  if (incoming) return 'source_ingress';
  if (outgoing) return 'source_egress';
  return 'normal';
}

export default function WebUiSettingsMirroringPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [rows, setRows] = React.useState<Row[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const applyDestinationRules = React.useCallback((nextRows: Row[]): Row[] => {
    const dest = nextRows.find((r) => r.destination)?.interface;
    if (!dest) return nextRows;
    return nextRows.map((r) =>
      r.interface === dest
        ? { ...r, incoming: false, outgoing: false, destination: true }
        : r,
    );
  }, []);

  const reload = React.useCallback(async () => {
    const [interfaces, mirror] = await Promise.all([
      postWebUiApi<PortInfo[]>({
        service: 'interface',
        access: 'get',
        command: 'get interfaces',
      }),
      postWebUiApi<{ src?: MirrorSrc[]; dest?: number }>({
        service: 'mirroring',
        access: 'get',
        command: 'get mirroring',
      }),
    ]);
    const srcByIf = new Map(
      (mirror.src ?? []).map((s) => [s.interface, s.state]),
    );
    const nextRows: Row[] = (interfaces ?? [])
      .slice()
      .sort((a, b) => a.interface - b.interface)
      .map((p) => {
        const state = srcByIf.get(p.interface) ?? 'normal';
        return {
          interface: p.interface,
          label: p.name || `P${p.interface}`,
          incoming: state === 'source_ingress' || state === 'source_both',
          outgoing: state === 'source_egress' || state === 'source_both',
          destination: mirror.dest === p.interface,
        };
      });
    setRows(applyDestinationRules(nextRows));
  }, [applyDestinationRules]);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  return (
    <WebUiPageWrapper titleList={['Settings', 'Port Mirroring']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-col gap-2">
        <h4 className="font-bold">Mirroring Settings</h4>
        <table className="webui-table min-w-96 max-w-fit">
          <thead>
            <tr className="webui-table-row-head">
              <th rowSpan={2}>Port</th>
              <th colSpan={2}>Traffic to mirror</th>
              <th rowSpan={2}>Destination</th>
            </tr>
            <tr className="webui-table-row-head">
              <th>Incoming</th>
              <th>Outgoing</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.interface} className="webui-table-row-content">
                <td className="text-center">{row.label}</td>
                <td className="text-center">
                  <WebUiInput
                    type="checkbox"
                    disabled={readonly || row.destination}
                    checked={row.incoming}
                    onChange={(e) =>
                      setRows((prev) =>
                        applyDestinationRules(
                          prev.map((r) =>
                            r.interface === row.interface
                              ? { ...r, incoming: e.target.checked }
                              : r,
                          ),
                        ),
                      )
                    }
                  />
                </td>
                <td className="text-center">
                  <WebUiInput
                    type="checkbox"
                    disabled={readonly || row.destination}
                    checked={row.outgoing}
                    onChange={(e) =>
                      setRows((prev) =>
                        applyDestinationRules(
                          prev.map((r) =>
                            r.interface === row.interface
                              ? { ...r, outgoing: e.target.checked }
                              : r,
                          ),
                        ),
                      )
                    }
                  />
                </td>
                <td className="text-center">
                  <WebUiInput
                    type="checkbox"
                    disabled={readonly}
                    checked={row.destination}
                    onChange={(e) =>
                      setRows((prev) => {
                        const next = prev.map((r) => {
                          if (r.interface === row.interface) {
                            return { ...r, destination: e.target.checked };
                          }
                          if (e.target.checked) {
                            return { ...r, destination: false };
                          }
                          return r;
                        });
                        return applyDestinationRules(next);
                      })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex w-full gap-2">
          <WebUiButton
            disabled={readonly}
            onClick={async () => {
              try {
                const payload: MirrorPayload = {
                  dest: rows.find((r) => r.destination)?.interface ?? -1,
                  src: rows.map((r) => ({
                    interface: r.interface,
                    state: stateFromChecks(r.incoming, r.outgoing),
                  })),
                };
                await postWebUiApi({
                  service: 'mirroring',
                  access: 'set',
                  command: 'set mirroring',
                  parameters: payload,
                });
                setMessage('Mirroring updated.');
                setError(null);
                await reload();
              } catch (err) {
                setError((err as Error).message);
              }
            }}
          >
            Set
          </WebUiButton>
          <WebUiButton
            onClick={() =>
              reload().catch((err) => setError((err as Error).message))
            }
          >
            Reload
          </WebUiButton>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
